import { DateTime } from "luxon";

const toLuxonDateTime = (timestamp: Date | string): DateTime =>
  DateTime.fromJSDate(typeof timestamp === "string" ? new Date(timestamp) : timestamp);

export const getDate = (timestamp: Date | string, format = "MMM dd") => toLuxonDateTime(timestamp).toFormat(format);

export const getTime = (timestamp: Date | string, format = "h:mma") => toLuxonDateTime(timestamp).toFormat(format);

export const getTimeRange = (beginTime: Date, endTime: Date) =>
  `${getTime(beginTime)} - ${getTime(endTime, "h:mma z")}`;

export const getTimeRangeWithDate = (beginTime: Date | string, endTime: Date | string) =>
  `${getTime(beginTime, "ccc, MMM d h:mma")} - ${getTime(endTime, "h:mma z")}`;

export const getIsInPast = (timestamp: Date | string) => toLuxonDateTime(timestamp) < DateTime.now().startOf("day");

export const generateMeetingTime = (beginTime?: Date | string, endTime?: Date | string) => {
  if (!beginTime || !endTime) {
    return "";
  }

  return `${getDate(beginTime)} ${getTime(beginTime)}-${getTime(endTime)}`;
};

export interface FormatEventTimesOptions {
  /** When true and the event is in the current year, omit the date (show only time for same-day) */
  omitDateWhenCurrentYear?: boolean;
}

export const formatEventTimes = (startTime: Date, endTime: Date, options?: FormatEventTimesOptions) => {
  if (endTime && startTime && endTime < startTime) {
    return "";
  }

  const currentYear = new Date().getFullYear();
  const isCurrentYear =
    options?.omitDateWhenCurrentYear &&
    startTime?.getFullYear() === currentYear &&
    (!endTime || endTime.getFullYear() === currentYear);

  const sameDay = endTime && startTime && startTime?.toDateString() === endTime?.toDateString();

  if (sameDay) {
    if (isCurrentYear) {
      return `${formatNumericEventTime(startTime)} - ${formatNumericEventTime(endTime)}`;
    }
    return `${formatEventTime(startTime)} - ${formatNumericEventTime(endTime)}`;
  }

  if (startTime && endTime) {
    if (isCurrentYear) {
      return `${formatEventDateWithoutYear(startTime)} ${formatNumericEventTime(startTime)} - ${formatEventDateWithoutYear(endTime)} ${formatNumericEventTime(endTime)}`;
    }
    return `${formatEventTime(startTime)} - ${formatEventTime(endTime)}`;
  }

  if (startTime && !endTime) {
    if (isCurrentYear) {
      return formatNumericEventTime(startTime);
    }
    return formatEventTime(startTime);
  }

  return "";
};

export const formatEventTime = (time: Date) => {
  if (!time) {
    return "";
  }

  return `${formatDefaultEventDate(time)} ${formatNumericEventTime(time)}`;
};

// Using undefined as the locale parameter makes these functions use the browser's default locale
const formatDefaultEventDate = (time: Date) => {
  return time.toLocaleDateString(undefined, {
    day: "numeric",
    month: "numeric",
    year: "2-digit",
  });
};

const formatEventDateWithoutYear = (time: Date) => {
  return time.toLocaleDateString(undefined, {
    day: "numeric",
    month: "numeric",
  });
};

const formatNumericEventTime = (time: Date) => {
  return time.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "numeric",
  });
};
